import { Controller } from "@hotwired/stimulus"

// Email combobox: shows a dropdown of known emails for the selected contact,
// but also allows freeform email entry. Updates when contact changes.
export default class extends Controller {
  static targets = ["input", "dropdown", "list", "hidden"]
  static values = {
    contactEmailsMap: Object, // { contactId: ["email1", "email2"] }
    contactInputSelector: String, // CSS selector for the contact hidden input
  }

  connect() {
    this.isOpen = false
    this.emails = []
    this.selectedIndex = -1

    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.handleClickOutside)

    // Watch for contact changes
    this._observeContactChanges()

    // Initialize from default contact
    this._updateEmailsFromContact()
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside)
  }

  _observeContactChanges() {
    const selector = this.contactInputSelectorValue
    if (!selector) return
    const contactInput = this.element.closest("form")?.querySelector(selector)
    if (!contactInput) return

    this._contactInput = contactInput
    contactInput.addEventListener("change", () => this._updateEmailsFromContact())
  }

  _updateEmailsFromContact() {
    const contactId = this._contactInput?.value
    const map = this.contactEmailsMapValue || {}
    this.emails = contactId ? (map[contactId] || []) : []

    // If there's exactly one email and the input is empty, pre-fill it
    if (this.emails.length === 1 && !this.inputTarget.value) {
      this.inputTarget.value = this.emails[0]
      this._syncHidden()
    }

    if (this.isOpen) this._render()
  }

  focus() {
    if (this.emails.length > 0) this.open()
  }

  open() {
    if (this.isOpen) return
    this.isOpen = true
    this.selectedIndex = -1
    if (this.hasDropdownTarget) this.dropdownTarget.classList.remove("hidden")
    this._render()
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false
    if (this.hasDropdownTarget) this.dropdownTarget.classList.add("hidden")
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target) && this.isOpen) this.close()
  }

  filter() {
    this.selectedIndex = -1
    this._render()
  }

  handleKeydown(event) {
    const filtered = this._filteredEmails()
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (!this.isOpen && this.emails.length > 0) { this.open(); return }
        this.selectedIndex = Math.min(this.selectedIndex + 1, filtered.length - 1)
        this._render()
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this._render()
        break
      case "Enter":
        event.preventDefault()
        if (this.selectedIndex >= 0 && this.selectedIndex < filtered.length) {
          this.inputTarget.value = filtered[this.selectedIndex]
        }
        this._syncHidden()
        this.close()
        break
      case "Escape":
        this.close()
        break
    }
  }

  selectEmail(event) {
    event.preventDefault()
    const email = event.currentTarget.dataset.email
    this.inputTarget.value = email
    this._syncHidden()
    this.close()
  }

  inputChanged() {
    this._syncHidden()
  }

  _syncHidden() {
    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = this.inputTarget.value.trim()
    }
  }

  _filteredEmails() {
    const query = this.inputTarget.value.toLowerCase().trim()
    if (!query) return this.emails
    return this.emails.filter(e => e.toLowerCase().includes(query))
  }

  _render() {
    if (!this.hasListTarget) return
    const filtered = this._filteredEmails()
    if (filtered.length === 0) {
      this.close()
      return
    }

    this.listTarget.innerHTML = filtered.map((email, i) => `
      <div class="px-3 py-1.5 cursor-pointer text-sm text-brand-body ${i === this.selectedIndex ? 'bg-brand-accent/10' : 'hover:bg-brand-accent/5'}"
           data-email="${this._escapeAttr(email)}"
           data-action="click->email-combobox#selectEmail">
        ${this._escapeHtml(email)}
      </div>
    `).join("")
  }

  _escapeHtml(text) {
    const d = document.createElement("div")
    d.textContent = text
    return d.innerHTML
  }

  _escapeAttr(text) {
    return text.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
